import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getFaqItems, getChatbotConfig } from '@/lib/api';

// Hard-coded fallback FAQ so chatbot never looks empty if the admin hasn't saved any yet.
const FALLBACK_FAQ = [
  {
    keywords: ['hours', 'open', 'time', 'when', 'available', 'monday', 'saturday'],
    question: 'What are your hours?',
    answer: "We're open Monday to Saturday, 8:00 AM – 5:00 PM (lunch break 1:00 PM – 1:30 PM). After-hours dyno tuning is available by arrangement — call William on 0278150446 to organise.",
  },
  {
    keywords: ['book', 'booking', 'appointment', 'schedule', 'reserve'],
    question: 'How do I book a session?',
    answer: 'Click on "DYNO BOOKINGS" in the menu, pick your date and time, then fill in your details.',
  },
  {
    keywords: ['contact', 'phone', 'email', 'call', 'reach'],
    question: 'How can I contact you?',
    answer: 'Call William at 0278150446 or email Nakitune@outlook.co.nz.',
  },
];

const DEFAULT_CONFIG = {
  welcome_message: "Hey! 👋 I'm the NAKI TUNE assistant. How can I help you today?",
  suggested_questions: [
    'What are your hours?',
    'How much does it cost?',
    'How do I book?',
    'Where are you located?',
  ],
  fallback_message:
    "I'm not sure about that. For specific questions, please contact William at 0278150446 or email Nakitune@outlook.co.nz. You can also book a session through our DYNO BOOKINGS page!",
  proactive_popup_enabled: true,
  proactive_popup_delay_seconds: 5,
  proactive_popup_message: "Got a question? I'm here to help! 💬",
};

const POPUP_DISMISSED_KEY = 'naki_chatbot_popup_dismissed';

export const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showProactive, setShowProactive] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [faq, setFaq] = useState(FALLBACK_FAQ);
  const [messages, setMessages] = useState([
    { type: 'bot', text: DEFAULT_CONFIG.welcome_message, time: new Date() },
  ]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  // Load config + FAQ from backend
  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, faqRes] = await Promise.all([
          getChatbotConfig(),
          getFaqItems(),
        ]);
        if (cfgRes?.data) {
          setConfig((prev) => ({ ...prev, ...cfgRes.data }));
          const welcome = cfgRes.data.welcome_message || DEFAULT_CONFIG.welcome_message;
          setMessages([{ type: 'bot', text: welcome, time: new Date() }]);
        }
        if (Array.isArray(faqRes?.data) && faqRes.data.length > 0) {
          setFaq(faqRes.data);
        }
      } catch (_e) {
        // Silent — keep defaults
      }
    })();
  }, []);

  // Proactive popup trigger — only once per session (sessionStorage), skipped if admin disabled
  useEffect(() => {
    if (!config.proactive_popup_enabled) return;
    if (isOpen) return;
    try {
      if (sessionStorage.getItem(POPUP_DISMISSED_KEY) === '1') return;
    } catch (_e) {
      // ignore
    }
    const delayMs = Math.max(0, (config.proactive_popup_delay_seconds || 5) * 1000);
    const t = setTimeout(() => setShowProactive(true), delayMs);
    return () => clearTimeout(t);
  }, [config.proactive_popup_enabled, config.proactive_popup_delay_seconds, isOpen]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const dismissProactive = () => {
    setShowProactive(false);
    try { sessionStorage.setItem(POPUP_DISMISSED_KEY, '1'); } catch (_e) { /* ignore */ }
  };

  const openChat = () => {
    setIsOpen(true);
    setShowProactive(false);
    try { sessionStorage.setItem(POPUP_DISMISSED_KEY, '1'); } catch (_e) { /* ignore */ }
  };

  const findAnswer = (query) => {
    const lowerQuery = query.toLowerCase();
    let bestMatch = null;
    let maxMatches = 0;
    for (const item of faq) {
      const kws = Array.isArray(item.keywords) ? item.keywords : [];
      const matches = kws.filter((keyword) => lowerQuery.includes(String(keyword).toLowerCase())).length;
      if (matches > maxMatches) {
        maxMatches = matches;
        bestMatch = item;
      }
    }
    // Also match on exact question text (great for the quick-tap buttons)
    if (!bestMatch) {
      const exact = faq.find((f) => (f.question || '').toLowerCase() === lowerQuery);
      if (exact) bestMatch = exact;
    }
    if (bestMatch) return bestMatch.answer;
    return config.fallback_message || DEFAULT_CONFIG.fallback_message;
  };

  const handleSend = (text = input) => {
    if (!text.trim()) return;
    const userMessage = { type: 'user', text: text.trim(), time: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setTimeout(() => {
      const answer = findAnswer(text);
      const botMessage = { type: 'bot', text: answer, time: new Date() };
      setMessages((prev) => [...prev, botMessage]);
    }, 400);
  };

  return (
    <>
      {/* Proactive popup bubble */}
      {showProactive && !isOpen && (
        <button
          onClick={openChat}
          className="fixed bottom-36 right-6 z-50 max-w-[260px] bg-zinc-900 border border-green-700 shadow-2xl rounded-2xl rounded-br-sm px-4 py-3 text-left animate-fade-in-up hover:border-green-500 transition-colors"
          data-testid="chatbot-proactive-popup"
        >
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-green-400 font-['Barlow_Condensed'] uppercase tracking-wider font-bold">NAKI TUNE</p>
              <p className="text-sm text-white leading-snug">{config.proactive_popup_message}</p>
            </div>
            <span
              onClick={(e) => { e.stopPropagation(); dismissProactive(); }}
              className="text-zinc-500 hover:text-white cursor-pointer flex-shrink-0"
              data-testid="chatbot-proactive-dismiss"
              role="button"
              tabIndex={0}
            >
              <X className="w-4 h-4" />
            </span>
          </div>
        </button>
      )}

      {/* Chat Button */}
      <button
        onClick={openChat}
        className={cn(
          'fixed bottom-20 right-6 z-50 w-14 h-14 bg-green-600 hover:bg-green-700 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110',
          isOpen && 'hidden',
        )}
        data-testid="chatbot-toggle"
      >
        <MessageCircle className="w-6 h-6 text-white" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-20 right-6 z-50 w-[350px] max-w-[calc(100vw-3rem)] h-[500px] max-h-[calc(100vh-6rem)] bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl flex flex-col overflow-hidden animate-fade-in-up">
          {/* Header */}
          <div className="bg-green-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-white" />
              <span className="font-['Barlow_Condensed'] font-bold text-white uppercase tracking-wide">
                NAKI TUNE Assistant
              </span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white"
              data-testid="chatbot-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={cn('max-w-[85%]', msg.type === 'user' ? 'ml-auto' : 'mr-auto')}
              >
                <div
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm whitespace-pre-wrap',
                    msg.type === 'user'
                      ? 'bg-green-600 text-white'
                      : 'bg-zinc-800 text-zinc-100 border border-zinc-700',
                  )}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 2 && (config.suggested_questions || []).length > 0 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-zinc-500 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {(config.suggested_questions || []).map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(q)}
                    className="text-xs px-3 py-1 bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white rounded-full transition-colors"
                    data-testid={`chatbot-quick-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-zinc-800">
            <form
              onSubmit={(e) => { e.preventDefault(); handleSend(); }}
              className="flex gap-2"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question..."
                className="flex-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                data-testid="chatbot-input"
              />
              <Button
                type="submit"
                size="icon"
                className="bg-green-600 hover:bg-green-700"
                data-testid="chatbot-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
